/**
 * Catalogo licenze (SuperAdmin MAE).
 *
 * v1.8.0: una `license` (catalogo, 103 voci) è effettivamente attivabile solo se
 * esiste un `fwk-addon` con stesso `code` e `isAvailable=true` (52 oggi).
 * Mostriamo tutte e marciamo "Coming soon" le voci a roadmap commerciale.
 */
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Badge, Card, Center, Group, Loader, ScrollArea, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { IconCrown, IconLicense, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { fetchList } from '@/shared/api/jsonapi/client';
import { EmptyState } from '@/shared/components/EmptyState';
import type { FwkAddon, License } from '@/shared/types/api.gen';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/admin/licenses-catalog')({ component: LicensesCatalogPage });

type LicenseRow = License & Denormalized & { id: string };
type AddonRow = FwkAddon & Denormalized & { id: string };

function LicensesCatalogPage() {
  const [search, setSearch] = useState('');

  const licensesQ = useQuery({
    queryKey: ['licenses-all'],
    staleTime: 5 * 60_000,
    queryFn: () => fetchList<LicenseRow>('licenses', { sort: ['category', 'code'], page: { size: 200 } }),
  });

  const addonsQ = useQuery({
    queryKey: ['fwk-addons-all'],
    staleTime: 5 * 60_000,
    queryFn: () => fetchList<AddonRow>('fwk-addons', { page: { size: 200 } }),
  });

  const addonByCode = useMemo(() => {
    const m = new Map<string, AddonRow>();
    for (const a of addonsQ.data?.data ?? []) {
      if (a.code) m.set(a.code, a);
    }
    return m;
  }, [addonsQ.data?.data]);

  const filtered = useMemo(() => {
    const rows = licensesQ.data?.data ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      (r.code ?? '').toLowerCase().includes(term)
      || (r.name ?? '').toLowerCase().includes(term)
      || (r.category ?? '').toLowerCase().includes(term),
    );
  }, [licensesQ.data?.data, search]);

  const totals = useMemo(() => {
    const total = licensesQ.data?.data?.length ?? 0;
    const available = (licensesQ.data?.data ?? []).filter((l) => l.code && addonByCode.get(l.code)?.isAvailable).length;
    return { total, available, comingSoon: total - available };
  }, [licensesQ.data?.data, addonByCode]);

  return (
    <Stack>
      <Group>
        <IconCrown size={28} />
        <Title order={1}>Catalogo licenze</Title>
      </Group>
      <Text c="dimmed" size="sm">
        Inventario completo licenze del framework. Una voce è effettivamente attivabile solo se esiste un <code>fwk-addon</code> con lo stesso codice marcato <code>isAvailable=true</code>.
      </Text>

      <Group>
        <Badge variant="light" color="blue" size="lg">Totali: {totals.total}</Badge>
        <Badge variant="light" color="green" size="lg">Attivabili: {totals.available}</Badge>
        <Badge variant="light" color="orange" size="lg">Roadmap: {totals.comingSoon}</Badge>
      </Group>

      {totals.comingSoon > 0 && (
        <Alert color="orange" variant="light" icon={<IconLicense size={14} />}>
          Le voci marcate <strong>Coming soon</strong> sono a catalogo ma il modulo associato non è ancora implementato: tentare di attivarle restituirà <code>403 LICENSE_NOT_IMPLEMENTED_YET</code>.
        </Alert>
      )}

      <TextInput
        placeholder="Cerca per codice, nome o categoria…"
        leftSection={<IconSearch size={14} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      <Card withBorder padding={0} radius="md">
        {licensesQ.isLoading || addonsQ.isLoading ? (
          <Center py="xl"><Loader /></Center>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="Nessun risultato"
            description="Prova a cambiare il termine di ricerca."
            py="lg"
          />
        ) : (
          <ScrollArea type="auto">
            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ minWidth: 200 }}>Codice</Table.Th>
                  <Table.Th>Nome</Table.Th>
                  <Table.Th style={{ minWidth: 140 }}>Categoria</Table.Th>
                  <Table.Th style={{ minWidth: 100 }}>Livello</Table.Th>
                  <Table.Th style={{ minWidth: 160 }}>Stato</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.map((l) => {
                  const addon = l.code ? addonByCode.get(l.code) : undefined;
                  return (
                    <Table.Tr key={l.id}>
                      <Table.Td><Text size="sm" ff="monospace">{l.code}</Text></Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>{l.name}</Text>
                        {l.description && <Text size="xs" c="dimmed">{l.description}</Text>}
                      </Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{l.category ?? '—'}</Text></Table.Td>
                      <Table.Td>{l.level && <Badge size="xs" variant="outline">{l.level}</Badge>}</Table.Td>
                      <Table.Td>
                        {!addon && <Badge color="gray" variant="light" size="sm">Nessun addon</Badge>}
                        {addon?.isAvailable && (
                          <Badge color="green" variant="light" size="sm">
                            Disponibile {addon.introducedInVersion ? `(${addon.introducedInVersion})` : ''}
                          </Badge>
                        )}
                        {addon && !addon.isAvailable && (
                          <Badge color="orange" variant="light" size="sm">Coming soon</Badge>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Card>
    </Stack>
  );
}
